import { AutoComplete, Button, Modal, TimeInput } from "@pickit/pickit-components";
import { Field, Form, FormSpy } from "react-final-form";
import { TemplateDetailModalPropsType } from "./types";
import { validationSchema } from "./schemas";
import "./TemplateDetailModal.scss";
import useValidationSchema from "hooks/useValidationSchema";
import FrequencyComponentContainer from "../frequencyCompoent/FrequencyComponentContainer";
import { Input } from "component/inputs/Input";
import { TemplateFormValues } from "../types";
import "./i18n";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

export const TemplateDetailModal: React.FC<TemplateDetailModalPropsType> = ({
  isOpen,
  regions,
  workzones,
  actualRegion,
  initialValues,
  fetchingWorkzones,
  testId,
  onClose,
  setDirty,
  onSubmit,
  onUpdateWorkzones,
  deleteTemplate,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (isOpen) {
      params.set('modal', 'template-detail');
    } else {
      params.delete('modal');
    }
    const paramsString = params.toString();
    const newUrl = paramsString ? `${window.location.pathname}?${paramsString}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }, [isOpen]);
  
  return (
    <Modal width="1190px" height="540px" isOpen={isOpen} onClose={onClose} testId={testId}>
      <div className="detail-modal-container">
        <h2 className="detail-modal-title" data-testId={`${testId}-title`}>
          {t("templateDetail:detail")}
        </h2>
        <hr className="detail-modal-separate" />
        <div className="container-template-detail-form">
          <Form
            onSubmit={(values: TemplateFormValues) => {}}
            validate={useValidationSchema(validationSchema)}
            initialValues={initialValues}
            mutators={{
              setValue: ([field, value], state, { changeValue }) => {
                if (field && typeof field === 'string') {
                  changeValue(state, field, () => value);
                }
              },
              setAutoCompleteValue: ([field], state, { changeValue }) => {
                if(!field.id) {
                  changeValue(state, "workzone", () => undefined);
                  return;
                }
                changeValue(state, field.id, () => field.name);
                if (state.lastFormState?.active === "region" && field.id !== actualRegion) {
                  changeValue(state, "workzone", () => undefined);
                  onUpdateWorkzones(field.id);
                }
              },
            }}
          >
            {({ handleSubmit, form, invalid, values, dirty }) => {
              const hasValidCapacity = values.frequency && 
                Object.values(values.frequency).some((capacityValue: number | null) => capacityValue !== null && capacityValue >= 0);
              
              return (
              <form className="form-template-detail" onSubmit={handleSubmit}>
                <FormSpy
                  subscription={{
                    dirty: true,
                    values: true,
                    invalid: true,
                  }}
                  onChange={(pro) => {
                    setDirty(pro.dirty);
                  }}
                />
                <div className="container-template-detail-form-row">
                  <div className="container-template-detail-form-col-1">
                    <Field
                      type="text"
                      name="code"
                      label={t("templateDetail:code")}
                      component={Input}
                      className="Admin-Pickers-input"
                      disabled={true}
                      testId={`${testId}-code`}
                    />
                  </div>
                  <div className="container-template-detail-form-col-1">
                    <Field
                      type="text"
                      name="updatedAt"
                      label={t("templateDetail:updatedAt")}
                      component={TimeInput}
                      className="Admin-Pickers-input"
                      disabled={true}
                      testId={`${testId}-updatedAt`}
                    />
                  </div>
                  <div className="container-template-detail-form-col-1">
                    <div className="frequency">
                      <FrequencyComponentContainer
                        name="frequency"
                        testId={`${testId}-frequency`}
                      />
                    </div>
                  </div>
                </div>
                <div className="container-template-detail-form-row">
                  <div className="container-template-detail-form-col-1">
                    <Field
                      type="text"
                      name="region"
                      className="Admin-Pickers-input"
                      onChange={form.mutators.setAutoCompleteValue}
                      label={t("templateDetail:regionLabel")}
                      placeholder={t("templateDetail:regionPlaceholder")}
                      options={regions}
                      testId={`${testId}-region`}
                      overAll
                      keyProperty="id"
                      showProperty="name"
                      isValueObject
                    >
                      {(props: any) => <AutoComplete {...props} />}
                    </Field>
                  </div>
                  <div className="container-template-detail-form-col-1">
                    <Field
                      type="text"
                      name="workzone"
                      className="Admin-Pickers-input"
                      onChange={form.mutators.setAutoCompleteValue}
                      label={t("templateDetail:workzoneLabel")}
                      placeholder={t("templateDetail:workzonePlaceholder")}
                      options={workzones}
                      disabled={workzones?.length === 0 || !values.region}
                      testId={`${testId}-workzone`}
                      overAll
                      keyProperty="id"
                      showProperty="name"
                      isValueObject
                      isLoading={fetchingWorkzones}
                    >
                      {(props: any) => (
                        <AutoComplete {...props} loading={fetchingWorkzones} />
                      )}
                    </Field>
                  </div>
                </div>
                <div className="container-template-detail-form-row">
                  <div className="container-template-detail-form-col-1">
                    <Field
                      type="text"
                      name="slotTime.initial"
                      label={t("templateDetail:slotStartLabel")}
                      component={TimeInput}
                      className="Admin-Pickers-input"
                      language="es"
                      placeholder={t("templateDetail:slotStartPlaceholder")}
                      testId={`${testId}-start-time`}
                      showClearIcon
                    />
                  </div>
                  <div className="container-template-detail-form-col-1">
                    <Field
                      name="slotTime.end"
                      label={t("templateDetail:slotEndLabel")}
                      component={TimeInput}
                      className="Admin-Pickers-input"
                      language="es"
                      placeholder={t("templateDetail:slotEndPlaceholder")}
                      testId={`${testId}-end-time`}
                      showClearIcon
                    />
                  </div>
                </div>
                <div className="detail-button-container">
                  <Button
                    type="button"
                    hierarchy="secondary"
                    width="220"
                    onClick={() => deleteTemplate()}
                    testId={`${testId}-cancel-button`}
                    label={t("global:delete")}
                  />

                  <Button
                    type="button"
                    hierarchy="primary"
                    width="220"
                    disabled={!dirty || !hasValidCapacity}
                    onClick={() => onSubmit(values)}
                    testId={`${testId}-confirm-button`}
                    label={t("global:update")}
                  />
                </div>
              </form>
            );
            }}
          </Form>
        </div>
      </div>
    </Modal>
  );
};
